// ============================================
// ConverseTech Frontend - Function Call Overlay
// ============================================

import { useEffect, useState, useRef } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Clock,
  ClipboardList,
  MessageCircleQuestion,
  X,
  Loader2,
} from 'lucide-react';
import type { GeminiFunctionCall } from '@conversetech/shared';

interface FunctionCallOverlayProps {
  functionCall: GeminiFunctionCall | null;
  onDismiss: () => void;
  isSpeaking?: boolean;
  autoDismissMs?: number;
}

// Function calls that need to wait for AI to finish speaking before starting timer
const TIMED_FUNCTION_CALLS: Record<string, number> = {
  provide_task_prompt: 0, // Uses time_limit_seconds from args
  request_repetition: 15, // 15 seconds to practice the phrase
};

const FUNCTION_CALL_CONFIG: Record<string, {
  icon: React.ReactNode;
  title: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}> = {
  interrupt_missing_section: {
    icon: <AlertTriangle className="w-6 h-6" />,
    title: 'Missing Section',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-400',
    textColor: 'text-yellow-800',
  },
  request_repetition: {
    icon: <RefreshCw className="w-6 h-6" />,
    title: 'Practice This Phrase',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-400',
    textColor: 'text-blue-800',
  },
  time_limit_warning: {
    icon: <Clock className="w-6 h-6" />,
    title: 'Time Warning',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-400',
    textColor: 'text-orange-800',
  },
  provide_task_prompt: {
    icon: <ClipboardList className="w-6 h-6" />,
    title: 'New Task',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-400',
    textColor: 'text-purple-800',
  },
  interview_followup: {
    icon: <MessageCircleQuestion className="w-6 h-6" />,
    title: 'Follow-up Question',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-400',
    textColor: 'text-green-800',
  },
};

export default function FunctionCallOverlay({
  functionCall,
  onDismiss,
  isSpeaking = false,
  autoDismissMs = 10000,
}: FunctionCallOverlayProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Use ref for onDismiss to avoid re-triggering effect
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Check if this function call needs timed display (wait for AI to finish speaking)
  const isTimedFunctionCall = functionCall?.name && functionCall.name in TIMED_FUNCTION_CALLS;

  // Handle visibility and auto-dismiss for non-timed function calls
  useEffect(() => {
    if (functionCall) {
      setIsVisible(true);
      setTimerStarted(false);
      setCountdown(null);

      // For timed function calls, don't auto-dismiss - let the timer handle it
      if (isTimedFunctionCall) {
        return;
      }

      // Auto-dismiss for other function calls
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismissRef.current(), 300);
      }, autoDismissMs);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setCountdown(null);
      setTimerStarted(false);
    }
    return undefined;
  }, [functionCall, autoDismissMs, isTimedFunctionCall]);

  // Start countdown when AI finishes speaking (for timed function calls)
  useEffect(() => {
    if (
      functionCall?.name &&
      isTimedFunctionCall &&
      !isSpeaking &&
      !timerStarted &&
      isVisible
    ) {
      // Get time limit from config or from args (for provide_task_prompt)
      let timeLimit = TIMED_FUNCTION_CALLS[functionCall.name];
      if (functionCall.name === 'provide_task_prompt') {
        timeLimit = Number(functionCall.args.time_limit_seconds) || 30;
      }
      setCountdown(timeLimit);
      setTimerStarted(true);
    }
  }, [functionCall, isSpeaking, timerStarted, isVisible, isTimedFunctionCall]);

  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown < 0) return;

    if (countdown === 0) {
      // Time's up - dismiss after a short delay
      const dismissTimer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismissRef.current(), 300);
      }, 2000);
      return () => clearTimeout(dismissTimer);
    }

    countdownRef.current = setTimeout(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown]);

  if (!functionCall) return null;

  const config = FUNCTION_CALL_CONFIG[functionCall.name] || {
    icon: <AlertTriangle className="w-6 h-6" />,
    title: 'Notice',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-400',
    textColor: 'text-gray-800',
  };

  const args = functionCall.args;

  return (
    <div
      data-testid="function-call-overlay"
      className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      }`}
    >
      <div className={`${config.bgColor} border-2 ${config.borderColor} rounded-xl shadow-lg p-4`}>
        <div className="flex items-start gap-3">
          <span className={config.textColor}>{config.icon}</span>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className={`font-semibold ${config.textColor}`}>{config.title}</h3>
              <button
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(onDismiss, 300);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content based on function type */}
            {renderFunctionContent(
              functionCall.name,
              args,
              config.textColor,
              isSpeaking,
              timerStarted,
              countdown
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderFunctionContent(
  name: string,
  args: Record<string, unknown>,
  textColor: string,
  isSpeaking: boolean,
  timerStarted: boolean,
  countdown: number | null
): React.ReactNode {
  switch (name) {
    case 'interrupt_missing_section':
      return (
        <div className="space-y-2">
          <p className={textColor}>
            You skipped the <strong>{String(args.missing_section)}</strong> section.
          </p>
          {args.suggestion ? (
            <p className="text-sm text-gray-600">
              Tip: {String(args.suggestion)}
            </p>
          ) : null}
        </div>
      );

    case 'request_repetition':
      return (
        <div className="space-y-3">
          {/* Timer display */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase">Repeat after me</span>
            <PracticeTimer
              isSpeaking={isSpeaking}
              timerStarted={timerStarted}
              countdown={countdown}
              totalTime={15}
            />
          </div>

          {args.original_phrase ? (
            <p className="text-sm text-gray-500 line-through">
              "{String(args.original_phrase)}"
            </p>
          ) : null}
          <p className={`font-medium ${textColor} text-lg`}>
            "{String(args.improved_phrase)}"
          </p>
          {args.reason ? (
            <p className="text-sm text-gray-600">{String(args.reason)}</p>
          ) : null}

          {/* Waiting for AI indicator */}
          {isSpeaking && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Listen to the correct pronunciation...</span>
            </div>
          )}

          {/* Start prompt */}
          {timerStarted && countdown !== null && countdown > 0 && (
            <div className="text-center pt-2 border-t border-blue-200">
              <span className="text-sm font-medium text-blue-700">
                Now repeat the phrase!
              </span>
            </div>
          )}
        </div>
      );

    case 'time_limit_warning':
      return (
        <div className="space-y-2">
          {args.time_exceeded ? (
            <p className={`font-medium ${textColor}`}>Time is up!</p>
          ) : (
            <p className={textColor}>
              <strong>{String(args.seconds_remaining)}</strong> seconds remaining
            </p>
          )}
          {args.message ? (
            <p className="text-sm text-gray-600">{String(args.message)}</p>
          ) : null}
        </div>
      );

    case 'provide_task_prompt':
      return (
        <div className="space-y-3">
          {/* Task type and timer */}
          <div className="flex items-center justify-between">
            <span className="uppercase font-medium text-sm text-gray-500">
              {String(args.task_type).replace('_', ' ')}
            </span>

            {/* Timer display */}
            <TaskTimer
              isSpeaking={isSpeaking}
              timerStarted={timerStarted}
              countdown={countdown}
              totalTime={Number(args.time_limit_seconds) || 30}
            />
          </div>

          {/* Task prompt */}
          <p className={`font-medium ${textColor}`}>{String(args.prompt)}</p>

          {args.instructions ? (
            <p className="text-sm text-gray-600">{String(args.instructions)}</p>
          ) : null}

          {/* Waiting for AI indicator */}
          {isSpeaking && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Waiting for instructions to finish...</span>
            </div>
          )}

          {/* Start prompt */}
          {timerStarted && countdown !== null && countdown > 0 && (
            <div className="text-center pt-2 border-t border-purple-200">
              <span className="text-sm font-medium text-purple-700">
                Start speaking now!
              </span>
            </div>
          )}
        </div>
      );

    case 'interview_followup':
      return (
        <div className="space-y-2">
          <span className="text-xs text-gray-500 uppercase">
            {String(args.followup_type).replace('_', ' ')}
          </span>
          <p className={`font-medium ${textColor}`}>{String(args.question)}</p>
          {args.context ? (
            <p className="text-sm text-gray-600 italic">
              Following up on: {String(args.context)}
            </p>
          ) : null}
        </div>
      );

    default:
      return (
        <pre className="text-sm text-gray-600 overflow-auto">
          {JSON.stringify(args, null, 2)}
        </pre>
      );
  }
}

// Timer component for practice/repetition
function PracticeTimer({
  isSpeaking,
  timerStarted,
  countdown,
  totalTime,
}: {
  isSpeaking: boolean;
  timerStarted: boolean;
  countdown: number | null;
  totalTime: number;
}) {
  if (isSpeaking) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-mono">--</span>
      </div>
    );
  }

  if (!timerStarted || countdown === null) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-mono">{totalTime}s</span>
      </div>
    );
  }

  const isLow = countdown <= 5;
  const colorClass = isLow ? 'text-orange-500' : 'text-blue-600';
  const bgColorClass = isLow ? 'bg-orange-100' : 'bg-blue-100';

  return (
    <div className={`flex items-center gap-2 px-2 py-0.5 rounded-full ${bgColorClass}`}>
      <Clock className={`w-3 h-3 ${colorClass}`} />
      <span className={`text-sm font-mono font-bold ${colorClass}`}>
        {countdown === 0 ? "Done!" : `${countdown}s`}
      </span>
    </div>
  );
}

// Timer component for task prompts
function TaskTimer({
  isSpeaking,
  timerStarted,
  countdown,
  totalTime,
}: {
  isSpeaking: boolean;
  timerStarted: boolean;
  countdown: number | null;
  totalTime: number;
}) {
  if (isSpeaking) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-mono">{totalTime}s</span>
      </div>
    );
  }

  if (!timerStarted || countdown === null) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-mono">{totalTime}s</span>
      </div>
    );
  }

  // Calculate progress and color
  const progress = countdown / totalTime;
  const isLow = countdown <= 10;
  const isCritical = countdown <= 5;

  const colorClass = isCritical
    ? 'text-red-600'
    : isLow
    ? 'text-orange-500'
    : 'text-purple-600';

  const bgColorClass = isCritical
    ? 'bg-red-100'
    : isLow
    ? 'bg-orange-100'
    : 'bg-purple-100';

  return (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${bgColorClass}`}>
      <Clock className={`w-4 h-4 ${colorClass}`} />
      <span className={`text-lg font-mono font-bold ${colorClass}`}>
        {countdown === 0 ? "Time's up!" : `${countdown}s`}
      </span>

      {/* Progress bar */}
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-1000 ease-linear ${
            isCritical ? 'bg-red-500' : isLow ? 'bg-orange-500' : 'bg-purple-500'
          }`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
}
